import React, { useEffect, useState } from "react";
import { Inbox, Send, FileEdit, Plus, Mail, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { WEBMAIL_EXTERNAL } from "../data/firmContent";

export default function Webmail() {
    const { user } = useAuth();
    const [folder, setFolder] = useState("inbox");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [composing, setComposing] = useState(false);
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/messages/${folder}`);
            setItems(data);
        } catch {
            toast.error("Could not load messages");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [folder]);

    const openMessage = async (m) => {
        setSelected(m);
        if (folder === "inbox" && !m.read) {
            await api.post(`/messages/${m.id}/read`).catch(() => {});
        }
    };

    return (
        <div data-testid="page-webmail">
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <div>
                        <div className="font-mono-cap text-xs text-[#c5a059] mb-2">Internal Webmail</div>
                        <h1 className="font-serif-display text-3xl md:text-4xl">Chamber inbox</h1>
                        <p className="mt-2 text-sm text-slate-600">Signed in as <span className="font-medium">{user?.email}</span></p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <button
                            onClick={() => setComposing(true)}
                            data-testid="webmail-compose-button"
                            className="inline-flex items-center gap-2 bg-[#0F172A] text-[#faf9f6] px-5 py-2.5 rounded-sm hover:bg-[#c5a059] hover:text-[#0F172A] transition-colors"
                        >
                            <Plus className="w-4 h-4" /> Compose
                        </button>
                        <a
                            href={WEBMAIL_EXTERNAL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="webmail-external-link"
                            className="inline-flex items-center gap-2 border border-slate-900/15 bg-white text-slate-800 px-5 py-2.5 rounded-sm hover:border-[#c5a059] transition-colors"
                        >
                            <ExternalLink className="w-4 h-4" /> External Webmail
                        </a>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid grid-cols-1 md:grid-cols-12 gap-px bg-slate-900/10 border border-slate-900/10">
                <aside className="md:col-span-3 bg-[#faf9f6] p-6 space-y-1">
                    {[
                        { id: "inbox", label: "Inbox", icon: Inbox },
                        { id: "sent", label: "Sent", icon: Send },
                        { id: "drafts", label: "Drafts", icon: FileEdit },
                    ].map((f) => (
                        <button
                            key={f.id}
                            onClick={() => { setFolder(f.id); setSelected(null); }}
                            data-testid={`webmail-folder-${f.id}`}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${
                                folder === f.id ? "bg-[#0F172A] text-[#faf9f6]" : "text-slate-700 hover:bg-[#f3f1ea]"
                            }`}
                        >
                            <f.icon className="w-4 h-4" /> {f.label}
                        </button>
                    ))}
                </aside>

                <div className="md:col-span-4 bg-white max-h-[70vh] overflow-y-auto">
                    {loading ? (
                        <div className="p-6 font-mono-cap text-xs text-slate-500">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="p-8 text-center text-sm text-slate-500">
                            <Mail className="w-8 h-8 mx-auto text-slate-300 mb-3" />
                            No messages.
                        </div>
                    ) : (
                        items.map((m) => (
                            <button
                                key={m.id}
                                onClick={() => openMessage(m)}
                                data-testid={`webmail-msg-${m.id}`}
                                className={`w-full text-left px-5 py-4 border-b border-slate-900/10 hover:bg-[#f3f1ea] transition-colors ${
                                    selected?.id === m.id ? "bg-[#f3f1ea]" : ""
                                } ${folder === "inbox" && !m.read ? "font-medium" : ""}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="text-sm text-slate-900 truncate">
                                        {folder === "sent" || folder === "drafts" ? `To: ${m.to_email}` : m.from_name || m.from_email}
                                    </div>
                                    <div className="font-mono-cap text-[10px] text-slate-400 shrink-0 ml-2">
                                        {new Date(m.created_at).toLocaleDateString()}
                                    </div>
                                </div>
                                <div className="text-sm text-slate-700 truncate mt-1">{m.subject}</div>
                                <div className="text-xs text-slate-500 truncate mt-1">{m.body.slice(0, 90)}</div>
                            </button>
                        ))
                    )}
                </div>

                <div className="md:col-span-5 bg-white p-8 min-h-[400px]">
                    {selected ? (
                        <div data-testid="webmail-message-view">
                            <h2 className="font-serif-display text-2xl text-slate-900">{selected.subject}</h2>
                            <div className="font-mono-cap text-[10px] text-slate-500 mt-2">
                                {folder === "sent" || folder === "drafts"
                                    ? `To ${selected.to_email}`
                                    : `From ${selected.from_name} <${selected.from_email}>`} · {new Date(selected.created_at).toLocaleString()}
                            </div>
                            <div className="rj-rule-gold w-12 my-6" />
                            <div className="text-slate-700 leading-relaxed whitespace-pre-line">{selected.body}</div>
                        </div>
                    ) : (
                        <div className="text-sm text-slate-400 font-mono-cap">Select a message to read.</div>
                    )}
                </div>
            </section>

            {composing && <ComposeModal onClose={() => { setComposing(false); load(); }} />}
        </div>
    );
}

function ComposeModal({ onClose }) {
    const [form, setForm] = useState({ to_email: "", subject: "", body: "" });
    const [busy, setBusy] = useState(false);

    const send = async (isDraft) => {
        if (!form.to_email || !form.subject) return toast.error("Recipient and subject are required");
        setBusy(true);
        try {
            await api.post("/messages", { ...form, is_draft: isDraft });
            toast.success(isDraft ? "Draft saved" : "Message sent");
            onClose();
        } catch (e) {
            toast.error("Could not send message");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="webmail-compose-modal">
            <div className="bg-[#faf9f6] w-full max-w-2xl border border-slate-900/10 rounded-sm">
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-900/10">
                    <div className="font-serif-display text-xl">New message</div>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="webmail-compose-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    <input
                        type="email" placeholder="To (email)"
                        value={form.to_email} onChange={(e) => setForm({ ...form, to_email: e.target.value })}
                        data-testid="webmail-compose-to"
                        className="w-full px-4 py-3 border border-slate-900/15 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#00a3e0]"
                    />
                    <input
                        placeholder="Subject"
                        value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                        data-testid="webmail-compose-subject"
                        className="w-full px-4 py-3 border border-slate-900/15 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#00a3e0]"
                    />
                    <textarea
                        rows={8} placeholder="Write your message…"
                        value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                        data-testid="webmail-compose-body"
                        className="w-full px-4 py-3 border border-slate-900/15 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#00a3e0]"
                    />
                </div>
                <div className="px-6 py-4 border-t border-slate-900/10 flex items-center justify-end gap-3">
                    <button onClick={() => send(true)} disabled={busy} data-testid="webmail-compose-draft" className="px-5 py-2.5 border border-slate-900/15 rounded-sm hover:border-[#c5a059] text-sm">Save draft</button>
                    <button onClick={() => send(false)} disabled={busy} data-testid="webmail-compose-send" className="px-5 py-2.5 bg-[#0F172A] text-[#faf9f6] rounded-sm hover:bg-[#c5a059] hover:text-[#0F172A] text-sm">{busy ? "Sending…" : "Send"}</button>
                </div>
            </div>
        </div>
    );
}
